import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Divider, Header, Icon, Label, List, Menu, Message, Segment, Tab } from 'semantic-ui-react';
import ReviewTab from '../../../components/admin/review/review';
import Loader from '../../../components/loader';
import RouteC from '../../../constnats/routeConstants';
import { getUpdateWithReviewsAndOwners } from '../../../service/tableUpdates';
import { serverToLocalDate } from '../../../utils/dateTimeUtils';
import CompanyRepo from '../../../utils/repository/companyRepo';
import { PathFactory } from '../../../utils/repository/utils';
import { canViewFullReview } from '../../../utils/reviewAccess';

type Owner = { fullName: string; disabled: boolean };

type Review = {
    id: number | string;
    type: string;
    status: string;
    ownerId: number | string;
    owner?: Owner;
    schemaId: number;
};

type Update = {
    id: number | string;
    description: string;
    summary: string;
    phase: string;
    purpose: string;
    ownerId: number | string;
    owner?: Owner;
    createdOn: string;
    updatedOn: string;
    reviews: Review[];
    srcSchemaId: number;
};

type Company = { id: number | string; name: string };

function OwnerLabel({ owner }: { owner?: Owner }) {
    if (!owner) {
        return <>—</>;
    }
    if (owner.disabled) {
        return (
            <Label color="red" size="mini">
                {owner.fullName}
            </Label>
        );
    }
    return <>{owner.fullName}</>;
}

function UpdateReviewsPage() {
    const { companyId, updateId } = useParams<'companyId' | 'updateId'>();
    const [update, setUpdate] = useState<Update | null>(null);
    const [company, setCompany] = useState<Company | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const srcSchemaId = update?.srcSchemaId;

    const fetchUpdate = useCallback(() => {
        if (!updateId || !companyId) {
            return;
        }
        setLoading(true);
        const updatePromise = getUpdateWithReviewsAndOwners(updateId);
        const companyPromise = new Promise<Company>((resolve) => {
            CompanyRepo.getInternal(companyId, resolve);
        });
        Promise.all([updatePromise, companyPromise]).then(([enrichedUpdate, companyData]) => {
            setUpdate(enrichedUpdate as Update);
            setCompany(companyData);
            setLoading(false);
        });
    }, [updateId, companyId]);

    useEffect(() => {
        fetchUpdate();
    }, [fetchUpdate]);

    if (loading) {
        return <Loader />;
    }

    if (!update) {
        return <Message error Description="Update not found." />;
    }

    const updatesTabUrl =
        companyId && srcSchemaId
            ? PathFactory.create([RouteC.EDIT_COMPANY_CONFIG_INTERNAL, companyId, 'updates', srcSchemaId])
            : null;

    const panes = update.reviews.map((r) => {
        const restricted = !canViewFullReview(r, update.phase, update.ownerId);
        const reportsUrl = companyId
            ? PathFactory.create([RouteC.EDIT_COMPANY_CONFIG_INTERNAL, companyId, 'reports', r.schemaId])
            : null;
        return {
            menuItem: (
                <Menu.Item key={r.id}>
                    {r.owner?.fullName ?? r.ownerId} — {r.type}
                    {reportsUrl && !restricted && (
                        <Link to={reportsUrl} onClick={(e) => e.stopPropagation()} style={{ marginLeft: 6 }}>
                            <Icon name="external alternate" />
                        </Link>
                    )}
                </Menu.Item>
            ),
            render: () => (
                <Tab.Pane>
                    <ReviewTab key={r.id} reviewId={r.id} restricted={restricted} onDeleted={fetchUpdate} onReviewStatusChange={fetchUpdate} />
                </Tab.Pane>
            ),
        };
    });

    return (
        <>
            <Header as="h2">
                <Link to={updatesTabUrl}>{update.summary}</Link>
                {' — Update '}
                {update.id}
            </Header>
            <Segment>
                <List horizontal divided>
                    <List.Item>
                        <List.Content>
                            <List.Header>Owner</List.Header>
                            <OwnerLabel owner={update.owner} />
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Phase</List.Header>
                            {update.phase}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Purpose</List.Header>
                            {update.purpose}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Created On</List.Header>
                            {serverToLocalDate(update.createdOn)}
                        </List.Content>
                    </List.Item>
                    <List.Item>
                        <List.Content>
                            <List.Header>Updated On</List.Header>
                            {serverToLocalDate(update.updatedOn)}
                        </List.Content>
                    </List.Item>
                </List>
                <Divider />
                <Header as="h5">Description</Header>
                <div>{update.description}</div>
            </Segment>
            {panes.length === 0 ? (
                <Message Description="This update has no reviews." />
            ) : (
                <Tab panes={panes} defaultActiveIndex={0} />
            )}
        </>
    );
}

export default UpdateReviewsPage;
